import { useMemo } from 'react';
import { Building2, Flame, CalendarClock, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { scoreColor } from '@/modules/sales-research/lib';
import { STAGE_LABEL, BOARD_COLUMNS, boardColumn } from './types';
import { relTime } from './widgets';
import type { PipelineRow } from './types';

export function PipelineBoard({ rows, loading, onOpen }: {
  rows: PipelineRow[]; loading: boolean; onOpen: (r: PipelineRow) => void;
}) {
  const byStage = useMemo(() => {
    const m = new Map<string, PipelineRow[]>();
    for (const s of BOARD_COLUMNS) m.set(s, []);
    for (const r of rows) {
      const key = boardColumn(r.status);
      if (key) m.get(key)!.push(r);
    }
    return m;
  }, [rows]);

  if (loading) {
    return <div className="flex gap-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-64 flex-1" />)}</div>;
  }

  return (
    <div className="flex gap-2 overflow-x-auto pb-2">
      {BOARD_COLUMNS.map((stage) => {
        const cards = byStage.get(stage) ?? [];
        return (
          <div key={stage} className="flex min-w-[180px] flex-1 flex-col rounded-lg border border-border/60 bg-card/30">
            <div className="flex items-center justify-between border-b border-border/60 px-3 py-2">
              <span className="text-xs font-semibold">{STAGE_LABEL[stage]}</span>
              <Badge variant="muted">{cards.length}</Badge>
            </div>
            <div className="flex max-h-[62vh] flex-col gap-2 overflow-y-auto p-2">
              {cards.length === 0 ? (
                <div className="flex flex-col items-center gap-1 py-6 text-center text-2xs text-muted-foreground">
                  <Building2 className="h-4 w-4 opacity-50" />
                </div>
              ) : cards.map((r) => (
                <button
                  key={r.id}
                  onClick={() => onOpen(r)}
                  className="rounded-md border border-border/60 bg-background p-2.5 text-left transition-colors hover:border-[#51BFAA]"
                >
                  <div className="flex items-start gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{r.name}</span>
                    <span
                      className="shrink-0 rounded px-1.5 py-0.5 font-mono text-2xs font-semibold text-white"
                      style={{ backgroundColor: scoreColor(r.score) }}
                    >
                      {r.score ?? '—'}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs text-muted-foreground">
                    {r.nearest_outlet_name && <span className="truncate">{r.nearest_outlet_name}</span>}
                    {r.warm_orders ? <span className="inline-flex items-center gap-0.5 text-[#51BFAA]"><Flame className="h-2.5 w-2.5" />{r.warm_orders}</span> : null}
                    {r.pending_drafts ? <span className="inline-flex items-center gap-0.5 text-[#B15599]"><FileText className="h-2.5 w-2.5" />{r.pending_drafts}</span> : null}
                    {r.next_followup_at && <span className="inline-flex items-center gap-0.5"><CalendarClock className="h-2.5 w-2.5" />{relTime(r.next_followup_at)}</span>}
                  </div>
                  {r.assigned_name && <div className="mt-1 truncate text-2xs text-muted-foreground">👤 {r.assigned_name}</div>}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
